import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import {
  Alert,
  Box,
  InputAdornment,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { makeStyles } from 'tss-react/mui';

import {
  exportPayoutsWorklist,
  getPayoutWorklistDetails,
  getPayoutsWorklist,
} from '../../api/paymentsApi/payoutsApi';
import NoDataText from '../../components/NoDataText';
import TablePaginationLocalContainer from '../../components/Table/TablePaginationLocalContainer';
import ViewCommonButton from '../../components/buttons/ViewCommonButton';
import { saveCsv } from '../../helpers/saveCsv';
import useSnackBar from '../../hooks/useSnackBar';
import { getReadablePayoutStatus } from './StatusBadge';
import PayoutDrawer from './PayoutDrawer';
import SummaryTabs from './SummaryTabs';
import WorklistFilters from './WorklistFilters';
import WorklistTable from './WorklistTable';
import { SHOW_PAYOUT_OWNER, SHOW_PAYOUT_STATUS_FILTER } from './featureFlags';
import {
  PayoutFilterValues,
  PayoutSummary,
  PayoutSummaryTab,
  PayoutWorklistDetailResponse,
  PayoutWorklistItem,
  PayoutWorklistQueryParams,
} from './types';

const DEFAULT_SUMMARY: PayoutSummary = {
  needsAttention: 0,
  inProgress: 0,
  completed: 0,
  all: 0,
};

const SEARCH_DEBOUNCE_MS = 400;

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  title: {
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
    fontSize: '30px',
    fontWeight: 600,
    marginBottom: '16px',
  },
  topControls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    gap: '12px',
    flexWrap: 'wrap',
  },
  searchControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexGrow: 1,
    minWidth: '340px',

    '@media (max-width: 700px)': {
      width: '100%',
      justifyContent: 'space-between',
      borderRadius: '12px',
      backgroundColor: theme.palette.primary.light,
      padding: '12px',
    },
  },
  search: {
    minWidth: '280px',
    maxWidth: '520px',
    flexGrow: 1,
    '& .MuiInputBase-root': {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary.light,
      borderRadius: '10px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.dark,
    },
  },
  filterToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    userSelect: 'none',

    '& .MuiSvgIcon-root': {
      color: theme.palette.common.white,
    },
  },
  filterToggleText: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '19px',
    color: theme.palette.common.white,
  },
  exportButtonWrapper: {
    '&.isExporting': {
      opacity: 0.6,
      pointerEvents: 'none',
    },
  },
  paginationRow: {
    marginTop: '0px',
  },
  tableSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  tableWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
}));

const parsePositiveNumber = (value: string | null, defaultValue: number) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : defaultValue;
};

const normalizeMethod = (method: string | null): string | undefined => {
  if (!method || method === 'All') {
    return undefined;
  }

  return method === 'Check' ? 'CHECK' : method;
};

const escapeCsvValue = (value: unknown) => {
  const rawValue = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(rawValue)
    ? `"${rawValue.replace(/"/g, '""')}"`
    : rawValue;
};

const formatLocalDateTime = (value?: string) => {
  if (!value) {
    return '';
  }

  return moment.utc(value).local().format('M/D/YYYY, h:mm:ss A');
};

const buildPayoutCsv = (items: PayoutWorklistItem[]) => {
  const headers = [
    'Settlement ID',
    'Claimant Name',
    'Claimant Reference ID',
    'Case Reference ID',
    'Amount',
    'Payment Method',
    'Onboarding Stage',
    'Funding Stage',
    'Distribution Stage',
    'Onboarding Status',
    'Updated At',
  ];

  const rows = items.map((item) => ([
    item.settlementId,
    item.claimant?.name || '-',
    item.claimant?.referenceId || '-',
    item.caseNumber || '-',
    item.amount ?? 0,
    item.paymentMethod || '-',
    item.stage?.onboarding || '-',
    item.stage?.funding || '-',
    item.stage?.distribution || '-',
    getReadablePayoutStatus(item.status || ''),
    formatLocalDateTime(item.updatedAt),
  ]));

  return [headers, ...rows]
    .map((row) => row.map(escapeCsvValue).join(','))
    .join('\n');
};

const PayoutsView = () => {
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();

  const [searchParams, setSearchParams] = useSearchParams();

  const page = parsePositiveNumber(searchParams.get('page'), 1);
  const pageSize = parsePositiveNumber(searchParams.get('pageSize'), 20);
  const sortBy = searchParams.get('sortBy') || 'updatedAt';
  const sortOrder = (searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc';
  const summaryTab = (searchParams.get('summaryTab') || 'all') as PayoutSummaryTab;
  const search = searchParams.get('search') || '';

  const filters = {
    stage: (searchParams.get('stage') || 'All') as PayoutFilterValues['stage'],
    status: (searchParams.get('status') || 'All Statuses') as PayoutFilterValues['status'],
    method: (searchParams.get('method') || 'All') as PayoutFilterValues['method'],
  };

  const selectedSettlementId = searchParams.get('settlementId')
    ? Number(searchParams.get('settlementId'))
    : undefined;
  const selectedSettlementSequenceId = searchParams.get('settlementSequenceId')
    ? Number(searchParams.get('settlementSequenceId'))
    : undefined;

  const [searchInput, setSearchInput] = useState(search);
  const [summary, setSummary] = useState<PayoutSummary>(DEFAULT_SUMMARY);
  const [rows, setRows] = useState<PayoutWorklistItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showFilters, setShowFilters] = useState(true);

  const [drawerData, setDrawerData] = useState<PayoutWorklistDetailResponse | null>(null);
  const [isDrawerLoading, setIsDrawerLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const updateSearchParams = (updates: Record<string, string | null>) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (!value || value === 'All') {
        nextParams.delete(key);
      } else {
        nextParams.set(key, value);
      }
    });

    setSearchParams(nextParams, { replace: true });
  };

  const queryParams = useMemo<PayoutWorklistQueryParams>(() => {
    return {
      page,
      pageSize,
      sortBy,
      sortOrder,
      summaryTab,
      search: search || undefined,
      stage: filters.stage !== 'All' ? filters.stage : undefined,
      status: SHOW_PAYOUT_STATUS_FILTER && filters.status !== 'All Statuses'
        ? filters.status
        : undefined,
      method: normalizeMethod(filters.method),
    };
  }, [page, pageSize, sortBy, sortOrder, search, filters.stage, filters.status, filters.method, summaryTab]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (searchInput !== search) {
        updateSearchParams({ search: searchInput || null, page: '1' });
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    setIsLoading(true);
    setErrorMessage('');

    getPayoutsWorklist(queryParams)
      .then((res) => {
        setSummary(res.data.summary || DEFAULT_SUMMARY);
        setRows(res.data.items || []);
        setTotalCount(res.data.pagination?.total || 0);
      })
      .catch((error) => {
        setErrorMessage(error?.response?.data?.message || 'Failed to load payouts worklist.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [queryParams]);

  useEffect(() => {
    if (!selectedSettlementId) {
      setDrawerData(null);
      return;
    }

    setIsDrawerLoading(true);
    getPayoutWorklistDetails(selectedSettlementId, selectedSettlementSequenceId)
      .then((res) => {
        setDrawerData(res.data || null);
      })
      .catch(() => {
        setAlert({ message: 'Failed to load payout details.', type: 'error' });
        setDrawerData(null);
      })
      .finally(() => {
        setIsDrawerLoading(false);
      });
  }, [selectedSettlementId, selectedSettlementSequenceId]);

  const onFiltersChange = (value: PayoutFilterValues) => {
    updateSearchParams({
      stage: value.stage,
      status: SHOW_PAYOUT_STATUS_FILTER ? value.status : null,
      method: value.method,
      page: '1',
    });
  };

  const onSummaryTabChange = (value: PayoutSummaryTab) => {
    updateSearchParams({ summaryTab: value, page: '1' });
  };

  const onSortChange = (field: string) => {
    const nextSortOrder = sortBy === field && sortOrder === 'desc' ? 'asc' : 'desc';
    updateSearchParams({ sortBy: field, sortOrder: nextSortOrder, page: '1' });
  };

  const onPageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    updateSearchParams({ page: String(value + 1) });
  };

  const onRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    const nextPageSize = Number(event.target.value);
    updateSearchParams({ pageSize: String(nextPageSize), page: '1' });
  };

  const onRowClick = (settlementId: number, settlementSequenceId?: number) => {
    updateSearchParams({
      settlementId: String(settlementId),
      settlementSequenceId: settlementSequenceId ? String(settlementSequenceId) : null,
    });
  };

  const onCloseDrawer = () => {
    updateSearchParams({ settlementId: null, settlementSequenceId: null });
  };

  const onExport = async () => {
    setIsExporting(true);

    try {
      const response = await exportPayoutsWorklist(queryParams);
      const csvContent = buildPayoutCsv(response.data?.items || []);
      saveCsv(csvContent, 'Payout_Worklist');
      setAlert({ message: 'Payout worklist exported successfully.', type: 'success' });
    } catch (error) {
      setAlert({ message: 'Failed to export payout worklist.', type: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  const onReset = () => {
    setSearchInput('');
    setSearchParams(
      {
        page: '1',
        pageSize: String(pageSize),
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        summaryTab: 'all',
      },
      { replace: true },
    );
  };

  const hasActiveFilters =
    Boolean(search) ||
    filters.stage !== 'All' ||
    (SHOW_PAYOUT_STATUS_FILTER && filters.status !== 'All Statuses') ||
    filters.method !== 'All';

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>Payout Worklist</Typography>

      <SummaryTabs value={summaryTab} summary={summary} onChange={onSummaryTabChange} />

      <Box className={classes.topControls}>
        <Box className={classes.searchControls}>
          <TextField
            className={classes.search}
            size="small"
            placeholder="Search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            inputProps={{ 'aria-label': 'Search payout worklist' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon htmlColor="#C3C4C8" />
                </InputAdornment>
              ),
            }}
          />

          <Box
            className={classes.filterToggle}
            onClick={() => setShowFilters((prev) => !prev)}
            role="button"
            aria-label="Toggle payout filters"
          >
            <FilterListIcon />
            <Typography className={classes.filterToggleText}>Filters</Typography>
          </Box>
        </Box>

        <Box display="flex" gap="8px">
          <Box
            className={`${classes.exportButtonWrapper} ${isExporting ? 'isExporting' : ''}`}
            aria-label="Export current payout worklist results"
          >
            <ViewCommonButton onClick={onExport} type="export" />
          </Box>
        </Box>
      </Box>

      {showFilters && (
        <WorklistFilters
          value={filters}
          onChange={onFiltersChange}
          onReset={onReset}
          resetDisabled={!hasActiveFilters}
          showStatus={SHOW_PAYOUT_STATUS_FILTER}
        />
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box className={classes.tableSection}>
        <Box className={classes.tableWrapper}>
          {!isLoading && rows.length === 0 && !errorMessage ? (
            <NoDataText marginTop="80px" />
          ) : (
            <WorklistTable
              rows={rows}
              loading={isLoading}
              selectedSettlementId={selectedSettlementId}
              selectedSettlementSequenceId={selectedSettlementSequenceId}
              onSortChange={onSortChange}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onRowClick={onRowClick}
              showOwner={SHOW_PAYOUT_OWNER}
            />
          )}
        </Box>

        <Box className={classes.paginationRow}>
          <TablePaginationLocalContainer
            count={totalCount}
            page={page - 1}
            rowsPerPage={pageSize}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            totalPages={totalPages}
          />
        </Box>
      </Box>

      <PayoutDrawer
        open={Boolean(selectedSettlementId)}
        loading={isDrawerLoading}
        data={drawerData}
        onClose={onCloseDrawer}
        showOwner={SHOW_PAYOUT_OWNER}
      />
    </Box>
  );
};

export default PayoutsView;
